import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { ScriptState } from "../../../types/script";
import { PURGE } from "redux-persist";
import { insertScriptAnalysis, insertFullScript, insertCharacters } from "src/app/db/queries";

// Fetches the script analysis from the external API
export const getAnalysis = createAsyncThunk<
    string, // Return type
    FormData, // Input type
    { rejectValue: string } // Error type
>(
    "script/getAnalysis",
    async (formData: FormData, { rejectWithValue }) => {
        try {
            // Fetch script analysis from API
            const res = await fetch(`${process.env.NEXT_PUBLIC_API_PG}`, {
            //const res = await fetch("/api/proxy", {
            method: "POST",
            //headers: {
            //    "Accept": "application/json",
            //},
            body: formData,
            });
            if (!res.ok) {
                throw new Error(`Response status: ${res.status}`);
            }

            const data = await res.json();

            if (!data || typeof data !== "object") {
                throw new Error("Invalid response format from API");
            }
            
            if (!data.id) {
                throw new Error("ID not found in API response");
            }
            // Insert script content into "full_scripts" table
            await insertFullScript(data.id, data);
            
            return data.id;

            /*const data = await res.json();

            // Get authenticated user
            const userId = data?.user_id; // Assuming the API response contains user ID

            if (!userId) {
                throw new Error("User ID not found in API response.");
            }

            // Insert script analysis into "scripts" table and get script ID
            const scriptId = await insertScriptAnalysis(data, userId);


            // Insert characters into "characters" table
            if (data.character_arcs && Array.isArray(data.character_arcs)) {
                await insertCharacters(scriptId, data.character_arcs);
            }

            // Return the final script data with the ID included
            return {
                result: { ...data, id: scriptId },
                isLoading: false,
                error: null,
            };*/
        } catch (error: any) {
            console.error(`Error in getAnalysis: ${error.message}`);
            return rejectWithValue(error.message || "Failed to fetch data");
        }
    }
);

export const processScript = createAsyncThunk<
    string, // Return type (We only need the response message or confirmation)
    string, // Input type (Script ID)
    { rejectValue: string } // Error type
>(
    "script/processScript",
    async (scriptId: string, { rejectWithValue }) => {
        try {
            //const apiUrl = "https://analisis-escenas-completo.onrender.com/procesar_guion";

            const res = await fetch(`${process.env.NEXT_PUBLIC_API_PROCESS_SCRIPT}`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({ GUION_ID: scriptId }),
            });

            if (!res.ok) {
                const errorText = await res.text();
                throw new Error(`API Error: ${res.status} - ${errorText}`);
            }

            //const data = await res.json();
            //console.log("API Response:", data);

            return "Processing successful"; // Return API message
        } catch (error: any) {
            console.error(`Error in processScript: ${error.message}`);
            return rejectWithValue(error.message || "Failed to process script");
        }
    }
);

export const analyzeArcIntensity = createAsyncThunk<
    string, // Return type (confirmation message)
    string, // Input type (Script ID)
    { rejectValue: string } // Error type
>(
    "script/analyzeArcIntensity",
    async (scriptId: string, { rejectWithValue }) => {
        try {
            const res = await fetch(`${process.env.NEXT_PUBLIC_API_ARC_INTENSITY}`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({ GUION_ID: scriptId }),
            });

            if (!res.ok) {
                const errorText = await res.text();
                throw new Error(`API Error: ${res.status} - ${errorText}`);
            }

            return "Dramatic Arc Analysis Successful"; // Return confirmation message
        } catch (error: any) {
            console.error(`Error in analyzeDramaticArc: ${error.message}`);
            return rejectWithValue(error.message || "Failed to analyze dramatic arc");
        }
    }
);

export const analyzeCharacters = createAsyncThunk<
    string, // Return type (confirmation message)
    string, // Input type (Script ID)
    { rejectValue: string } // Error type
>(
    "script/analyzeCharacters",
    async (scriptId: string, { rejectWithValue }) => {
        try {
            const res = await fetch(`${process.env.NEXT_PUBLIC_API_ANALYZE_CHARACTERS}/${scriptId}`, {
                method: "POST",
            });

            if (!res.ok) {
                const errorText = await res.text();
                throw new Error(`API Error: ${res.status} - ${errorText}`);
            }

            return "Character Analysis Successful"; // Return confirmation message
        } catch (error: any) {
            console.error(`Error in analyzeCharacters: ${error.message}`);
            return rejectWithValue(error.message || "Failed to analyze characters");
        }
    }
);

export const updateCharactersScene = createAsyncThunk<
    string, // Return type (confirmation message)
    string, // Input type (Script ID)
    { rejectValue: string } // Error type
>(
    "script/updateCharactersScene",
    async (scriptId: string, { rejectWithValue }) => {
        try {
            const res = await fetch(`${process.env.NEXT_PUBLIC_API_UPDATE_CHARACTERS_SCENE}/${scriptId}`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({ GUION_ID: scriptId }),
            });

            if (!res.ok) {
                const errorText = await res.text();
                throw new Error(`API Error: ${res.status} - ${errorText}`);
            }

            return "Characters Scene Update Successful"; // Return confirmation message
        } catch (error: any) {
            console.error(`Error in updateCharactersScene: ${error.message}`);
            return rejectWithValue(error.message || "Failed to update characters scene");
        }
    }
);

export const processCharacters = createAsyncThunk<
    string, // Return type (confirmation message)
    string, // Input type (Character ID)
    { rejectValue: string } // Error type
>(
    "script/processCharacters",
    async (scriptId: string, { rejectWithValue }) => {
        try {
            const res = await fetch(`${process.env.NEXT_PUBLIC_API_CHARACTER_PICTURE}?id_script=${scriptId}`, {
                method: "GET",
            });            

            if (!res.ok) {
                const errorText = await res.text();
                throw new Error(`API Error: ${res.status} - ${errorText}`);
            }

            return "Processing successful"; // Return confirmation message
        } catch (error: any) {
            console.error(`Error in processCharacters: ${error.message}`);
            return rejectWithValue(error.message || "Failed to process characters");
        }
    }
);

export const fetchStoryboardLookbook = createAsyncThunk<
    string, // Return type (confirmation message)
    string, // Input type (Script ID)
    { rejectValue: string } // Error type
>(
    "script/fetchStoryboardLookbook",
    async (scriptId: string, { rejectWithValue }) => {
        try {
            const res = await fetch(`${process.env.NEXT_PUBLIC_API_LOOKBOOK_STORYBOARD}?id_script=${scriptId}`, {
                method: "GET",
            });

            if (!res.ok) {
                const errorText = await res.text();
                throw new Error(`API Error: ${res.status} - ${errorText}`);
            }

            return "Storyboard & Lookbook Processing Successful"; // Return confirmation message
        } catch (error: any) {
            console.error(`Error in fetchStoryboardLookbook: ${error.message}`);
            return rejectWithValue(error.message || "Failed to fetch Storyboard Lookbook");
        }
    }
);

export const getExhaustiveSceneAnalysis = createAsyncThunk<
    string, // Return type
    string, // Input type (Script ID)
    { rejectValue: string } // Error type
>(
    "script/getExhaustiveSceneAnalysis",
    async (scriptId: string, { rejectWithValue }) => {
        try {
            const res = await fetch(`${process.env.NEXT_PUBLIC_API_EXHAUSTIVE_SCENE_ANALYSIS}/${scriptId}`, {
                method: "POST",
            });

            if (!res.ok) {
                const errorText = await res.text();
                throw new Error(`API Error: ${res.status} - ${errorText}`);
            }

            return "Exhaustive Scene Analysis Successful";
        } catch (error: any) {
            console.error(`Error in getExhaustiveSceneAnalysis: ${error.message}`);
            return rejectWithValue(error.message || "Failed to get exhaustive scene analysis");
        }
    }
);

export const getBudgetEstimation = createAsyncThunk<
    string, // Return type
    string, // Input type (Script ID)
    { rejectValue: string } // Error type
>(
    "script/getBudgetEstimation",
    async (scriptId: string, { rejectWithValue }) => {
        try {
            const res = await fetch(`${process.env.NEXT_PUBLIC_API_BUDGET_ESTIMATION}?id_script=${scriptId}`, {
                method: "POST",
            });

            if (!res.ok) {
                const errorText = await res.text();
                throw new Error(`API Error: ${res.status} - ${errorText}`);
            }

            return "Budget Estimation Successful";
        } catch (error: any) {
            console.error(`Error in getBudgetEstimation: ${error.message}`);
            return rejectWithValue(error.message || "Failed to get budget estimation");
        }
    }
);

// Initial state for the Redux slice
const initialState: ScriptState = {
    result: {
        synopsis: "",
        general_analysis: {},
        following_aspects: {},
        following_on_a_scale: {},
        detailed_analysis: {},
        break_down: {},
        takeaways_and_themes: {},
        character_arcs: [],
        suggestions_and_recommendations: {},
        pros_and_cons: {},
        id: "",
        scripts: [],
    },
    isLoading: false,
    error: null,
};

// Redux slice for handling script analysis state
export const scriptSlice = createSlice({
    name: "script",
    initialState,
    reducers: {
        clearScriptData: () => initialState,
    },
    extraReducers: (builder) => {
        builder
            .addCase(getAnalysis.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(getAnalysis.fulfilled, (state, action: PayloadAction<string>) => {
                state.isLoading = false;
                state.result = {
                    id: action.payload,
                    scripts: [], 
                  };
            })
            .addCase(getAnalysis.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload as string;
            })
            .addCase(analyzeArcIntensity.fulfilled, (state, action: PayloadAction<string>) => {
                state.isLoading = false;
                console.log("Dramatic Arc Analysis:", action.payload);
            })
            .addCase(analyzeArcIntensity.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload as string;
            })
            .addCase(analyzeCharacters.fulfilled, (state, action: PayloadAction<string>) => {
                state.isLoading = false;
                console.log("Character Analysis:", action.payload);
            })
            .addCase(analyzeCharacters.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload as string;
            })
            .addCase(processCharacters.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(processCharacters.fulfilled, (state, action: PayloadAction<string>) => {
                state.isLoading = false;
                console.log("Character Processing:", action.payload);
            })
            .addCase(processCharacters.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload as string;
            })
            .addCase(fetchStoryboardLookbook.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(fetchStoryboardLookbook.fulfilled, (state, action: PayloadAction<string>) => {
                state.isLoading = false;
                console.log("Storyboard & Lookbook Processing:", action.payload);
            })
            .addCase(fetchStoryboardLookbook.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload as string;
            })
            .addCase(getExhaustiveSceneAnalysis.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(getExhaustiveSceneAnalysis.fulfilled, (state, action: PayloadAction<string>) => {
                state.isLoading = false;
                console.log("Exhaustive Scene Analysis:", action.payload);
            })
            .addCase(getExhaustiveSceneAnalysis.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload as string;
            })
            .addCase(getBudgetEstimation.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(getBudgetEstimation.fulfilled, (state, action: PayloadAction<string>) => {
                state.isLoading = false;
                console.log("Budget Estimation:", action.payload);
            })
            .addCase(getBudgetEstimation.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload as string;
            })
            .addCase(PURGE, () => initialState);
    },
});

// Export actions and reducer
export const { clearScriptData } = scriptSlice.actions;
export const scriptReducer = scriptSlice.reducer;
